import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Translations {
  [key: string]: any;
}

interface LanguageConfig {
  [key: string]: string;
}

interface TranslationContextType {
  currentLanguage: string;
  changeLanguage: (language: string) => void;
  t: (key: string, defaultValue?: string) => string;
  languages: LanguageConfig;
  loading: boolean;
  supportedLanguages: string[];
}

const SUPPORTED_LANGUAGES = ['it', 'en', 'pt', 'es', 'de'];
const DEFAULT_LANGUAGE = 'it';
const STORAGE_KEY = 'gelato_language';

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

interface TranslationProviderProps {
  children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<string>(DEFAULT_LANGUAGE);
  const [translations, setTranslations] = useState<Translations>({});
  const [languages, setLanguages] = useState<LanguageConfig>({});
  const [loading, setLoading] = useState(true);

  // Carica la lingua salvata nel localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem(STORAGE_KEY);
    if (savedLanguage && SUPPORTED_LANGUAGES.includes(savedLanguage)) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Carica le traduzioni quando cambia la lingua
  useEffect(() => {
    const loadTranslations = async () => {
      setLoading(true);
      try {
        const response = await import(`../locales/${currentLanguage}.json`);
        setTranslations(response.default);
        setLanguages(response.default.languages);
      } catch (error) {
        console.error(`Errore nel caricamento traduzioni per ${currentLanguage}:`, error);
        // Fallback alla lingua di default
        if (currentLanguage !== DEFAULT_LANGUAGE) {
          try {
            const fallbackResponse = await import(`../locales/${DEFAULT_LANGUAGE}.json`);
            setTranslations(fallbackResponse.default);
            setLanguages(fallbackResponse.default.languages);
          } catch (fallbackError) {
            console.error('Errore nel caricamento traduzioni di fallback:', fallbackError);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    loadTranslations();
  }, [currentLanguage]);

  // Funzione per cambiare lingua
  const changeLanguage = (newLanguage: string) => {
    if (SUPPORTED_LANGUAGES.includes(newLanguage)) {
      setCurrentLanguage(newLanguage);
      localStorage.setItem(STORAGE_KEY, newLanguage);
    }
  };

  // Funzione per ottenere una traduzione
  const t = (key: string, defaultValue?: string): string => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return defaultValue || key;
      }
    }
    
    return typeof value === 'string' ? value : defaultValue || key;
  };

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    languages,
    loading,
    supportedLanguages: SUPPORTED_LANGUAGES
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslations = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslations must be used within a TranslationProvider');
  }
  return context;
};

export default useTranslations;
